import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type { Numeric } from '@/utils/numeric';
import type { LiveRainSkip } from './espnowIrrigation';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface OverviewRouter {
  router_mac: string;
  name: string | null;
  online: boolean;
  lat: number | null;
  lng: number | null;
  relay_states: number; // 8-bit bitmask
  /**
   * When the controller last told the server this node's output bitmask — set
   * on every relay-state report, including the unchanged one a GET_STATUS asks
   * for. It is what gives `relay_states` an age: without it a zero bit cannot
   * be read, because a valve reported off a second ago (its program did not
   * fire) looks exactly like one nobody has heard from in an hour.
   *
   * Null when the node has never reported one at all.
   */
  relay_state_updated_at: string | null;
  rssi_dbm: number | null;
  relay_labels: Record<string, string>;
  last_seen: string | null;
  /**
   * Relays the board physically has. Null when nobody has said — pre-capability
   * ESP-NOW firmware, or any XBee node, whose ND discovery carries no IO
   * capability. Read it through `relayCountFor`, never raw: the fallback
   * depends on the controller's transport.
   */
  relay_count: number | null;
}

export interface OverviewZone {
  router_mac: string;
  relay_index: number;
  zone_name: string | null;
  zone_color: string;
  boundary_geometry: GeoJSONPolygon | null;
}

export interface OverviewController {
  controller_id: string;
  controller_name: string | null;
  online: boolean;
  /**
   * Radio this site reaches its nodes over: espnow | xbee | lora | http.
   *
   * It decides where the watering timers live, and so which feed says what is
   * running. An `xbee` site's laptop bridge holds its own timers — nothing is
   * ever pushed to its nodes — so those nodes report no armed windows, and a
   * reader that does not know the transport cannot tell that apart from a site
   * with nothing scheduled.
   */
  transport: string | null;
  /**
   * Set while watering is held here — somebody pressed Stop and has not
   * resumed. Carried on the overview because this page answers "what is
   * happening on the farm right now?", and a site that will not water until
   * somebody says so is the most important answer it can give. Without it the
   * page says "no valves are open", which is true and hides the reason.
   */
  watering_paused_at: string | null;
  watering_pause_reason: string | null;
  routers: OverviewRouter[];
  zones: OverviewZone[];
  /** Valves rain skipped today. Absent from a server that predates rain skips. */
  rain_skips_today?: LiveRainSkip[];
}

export interface OverviewSensor {
  device_id: string;
  name: string | null;
  type_key: string;
  type_display_name: string | null;
  type_unit: string | null;
  type_icon: string | null;
  online: boolean;
  lat: number | null;
  lng: number | null;
  latest_value: number | null;
  latest_reading_time: string | null;
}

export interface WeatherObservation {
  observed_at: string;
  temp_c: Numeric;
  humidity: Numeric;
  wind_kph: Numeric;
  wind_gust_kph: Numeric;
  wind_dir: number | null;
  rain_mm_hour: Numeric;
  rain_mm_day: Numeric;
  solar_wm2: Numeric;
  uv: Numeric;
  pressure_hpa: Numeric;
  dew_point_c: Numeric;
  feels_like_c: Numeric;
}

export interface OverviewWeatherStation {
  id: number;
  name: string;
  lat: number | null;
  lng: number | null;
  active: boolean;
  latest_observation: WeatherObservation | null;
}

export interface OverviewFarm {
  id: number;
  name: string;
  boundary_geometry: GeoJSONPolygon | null;
  center_point: { lat: number; lng: number } | null;
  total_area_hectares: Numeric;
  controllers: OverviewController[];
  sensors: OverviewSensor[];
  weather_stations: OverviewWeatherStation[];
}

export interface UnassignedDevices {
  controllers: OverviewController[];
  sensors: OverviewSensor[];
  weather_stations: OverviewWeatherStation[];
}

export interface IrrigationOverviewData {
  farms: OverviewFarm[];
  unassigned: UnassignedDevices;
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function getOverview(): Promise<IrrigationOverviewData> {
  const res = await apiService.get<IrrigationOverviewData>('/irrigation/overview');
  return unwrap(res);
}

/** Check if bit `relayIndex` (0-7) is set in the bitmask */
export function isRelayOn(bitmask: number, relayIndex: number): boolean {
  return (bitmask & (1 << relayIndex)) !== 0;
}

export default { getOverview };
